import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { WEBSOCKET_URLS } from '@/shared/consts';
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  Popper,
  styled,
  useTheme,
} from '@mui/material';
import { dispatch } from '@/redux';
import { Room, User } from '../types';
import { useHttpMutation } from '@/api';
import { API_URLS } from '../consts';
import { BhabiStorage, soundEffect } from '@/shared/lib';
import { getUser } from '@/redux/reducers';
import { IN_PLAY_ROUTE } from '../in-play';
import { useNavigate } from 'react-router-dom';

import { inviteStatus } from './room-status';
import './social.css';
import { useWebsocket } from '../use-websocket';
import {
  NotEnoughCoins,
  useAlert,
  useMatchEntryCoins,
} from '@/shared/components';
import { roomErrorMessage } from '@/screens/guest/lib/room-errors';
import { Bell } from 'lucide-react';

export const InviteNotifierWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(1),
  left: theme.spacing(1),
  zIndex: 1000,
  cursor: 'pointer',
}));

export declare type InviteNotificationResponse = {
  id: string;
  userID: string;
  fromUser: User;
  to: string;
  toUser: User;
  roomID: string;
  room: Room;
  status: 'pending' | 'rejoin';
  createdAt: string;
  updatedAt: string;
};

const menuId = 'primary-notification-menu';

type PendingAction = { id: string; kind: 'accept' | 'rejoin' | 'decline' };

export const InviteNotifier: FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isInviteMenuOpen = Boolean(anchorEl);
  // Which invite has a request in flight, so only its button reads busy.
  const [pending, setPending] = useState<PendingAction | null>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const { mutate: rejectInvite, isLoading: isRejectInviteLoading } =
    useHttpMutation<unknown, { inviteID: string }>(
      API_URLS.REJECT_INVITE,
      'POST'
    );

  const { mutate: joinRoom, isLoading: isJoinRoomLoading } = useHttpMutation<
    { token: string },
    { roomID: string }
  >(API_URLS.JOIN_ROOM, 'POST');

  const { mutate: reJoinRoom, isLoading: isRejoinRoomLoading } =
    useHttpMutation<{ token: string }, { roomID: string }>(
      API_URLS.RE_JOIN_ROOM,
      'POST'
    );

  const nav = useNavigate();
  const { addAlert } = useAlert();
  const { sendMessage, socketResponse: lastJsonMessage } = useWebsocket<{
    invites: InviteNotificationResponse[];
  }>(WEBSOCKET_URLS().INVITE_NOTIFICATION);

  useEffect(() => {
    const interval = setInterval(() => {
      sendMessage('Ping');
    }, 2000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(prev => (!prev ? event.currentTarget : null));
  }, []);

  const rejectInviteHandler = useCallback(
    (inviteID: string) => {
      setPending({ id: inviteID, kind: 'decline' });
      rejectInvite(
        { inviteID },
        {
          onSuccess: () => {
            setPending(null);
            sendMessage('update');
          },
          onError: err => {
            setPending(null);
            addAlert({
              type: 'error',
              message: roomErrorMessage(
                err,
                "Couldn't decline this invite. Try again."
              ),
            });
            sendMessage('update');
          },
        }
      );
    },
    [rejectInvite, sendMessage, addAlert]
  );

  const {
    checkIfEnoughCoins,
    hasEnoughCoins,
    showCoinsMessage,
    setShowCoinsMessage,
  } = useMatchEntryCoins();

  const acceptInviteHandler = useCallback(
    (roomID: string, inviteID: string) => {
      if (!hasEnoughCoins) {
        checkIfEnoughCoins();
        return;
      }

      setPending({ id: inviteID, kind: 'accept' });
      joinRoom(
        { roomID },
        {
          onSuccess: res => {
            setPending(null);
            setAnchorEl(null);
            BhabiStorage.setToken(res.token);
            dispatch(getUser(res.token));
            nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
          },
          onError: err => {
            setPending(null);
            addAlert({
              type: 'error',
              message: roomErrorMessage(
                err,
                "Couldn't join this table. Try again."
              ),
            });
            // refresh: the invite may be for a table that filled or closed
            sendMessage('update');
          },
        }
      );
    },
    [joinRoom, nav, hasEnoughCoins, checkIfEnoughCoins, addAlert, sendMessage]
  );

  const rejoinInviteHandler = useCallback(
    (roomID: string, inviteID: string) => {
      setPending({ id: inviteID, kind: 'rejoin' });
      reJoinRoom(
        { roomID },
        {
          onSuccess: res => {
            setPending(null);
            setAnchorEl(null);
            BhabiStorage.setToken(res.token);
            dispatch(getUser(res.token));
            nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
          },
          onError: err => {
            setPending(null);
            addAlert({
              type: 'error',
              message: roomErrorMessage(
                err,
                "Couldn't rejoin this table. Try again."
              ),
            });
            sendMessage('update');
          },
        }
      );
    },
    [nav, reJoinRoom, addAlert, sendMessage]
  );

  const anyBusy =
    !!pending || isJoinRoomLoading || isRejoinRoomLoading || isRejectInviteLoading;

  const renderInvite = (n: InviteNotificationResponse) => {
    const isRejoin = n.status === 'rejoin';
    const availability = inviteStatus(n.room);
    const mine = pending?.id === n.id ? pending.kind : null;
    const from = n.fromUser?.name || 'A player';
    const primaryLabel = isRejoin
      ? mine === 'rejoin'
        ? 'Rejoining…'
        : 'Rejoin'
      : mine === 'accept'
      ? 'Joining…'
      : 'Accept';
    return (
      <>
        <p className="bh-invites__title">
          <strong title={from}>{from}</strong>
          <span>
            {isRejoin ? ' is back at your table' : ' invited you to a table'}
          </span>
        </p>
        <p
          className="bh-invites__meta"
          data-tone={isRejoin || availability.canAccept ? 'open' : 'full'}
        >
          {isRejoin ? `${n.room.numberOfPlayers}-player table` : availability.label}
        </p>
        <div className="bh-invites__actions">
          <button
            type="button"
            className="bh-btn bh-btn--secondary bh-btn--small"
            disabled={anyBusy}
            aria-busy={mine === 'decline'}
            onClick={() => rejectInviteHandler(n.id)}
          >
            {mine === 'decline' ? 'Declining…' : 'Decline'}
          </button>
          <button
            type="button"
            className="bh-btn bh-btn--primary bh-btn--small"
            disabled={anyBusy || (!isRejoin && !availability.canAccept)}
            aria-busy={mine === 'accept' || mine === 'rejoin'}
            onClick={() =>
              isRejoin
                ? rejoinInviteHandler(n.roomID, n.id)
                : acceptInviteHandler(n.roomID, n.id)
            }
          >
            {primaryLabel}
          </button>
        </div>
      </>
    );
  };

  const inviteCount = lastJsonMessage?.invites?.length ?? 0;

  const lastNotification = useRef<number | null>(null);
  useEffect(() => {
    if (!lastJsonMessage?.invites?.length) return;

    if (!lastNotification.current) {
      soundEffect.playSound(soundEffect.notification);
      lastNotification.current = lastJsonMessage?.invites?.length;
    }
  }, [lastJsonMessage?.invites?.length]);

  return (
    <>
      <NotEnoughCoins
        showCoinsMessage={showCoinsMessage}
        setShowCoinsMessage={setShowCoinsMessage}
      />
      <IconButton
        ref={bellRef}
        aria-label={
          inviteCount
            ? `Game invitations, ${inviteCount} waiting`
            : 'Game invitations'
        }
        aria-haspopup="dialog"
        aria-expanded={isInviteMenuOpen}
        aria-controls={isInviteMenuOpen ? menuId : undefined}
        sx={{ width: 44, height: 44 }}
        onClick={handleMenuOpen}
      >
        <Badge
          badgeContent={inviteCount}
          color="success"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.success.main,
              boxShadow: `0 0 0 1.5px ${theme.palette.background.paper}`,
              // Compact pill so it doesn't dwarf the 20px bell icon.
              height: 14,
              minWidth: 14,
              padding: '0 4px',
              fontSize: '0.625rem',
              lineHeight: 1,
              fontWeight: 700,
              top: 2,
              right: 2,
            },
          }}
        >
          <Bell size={20} strokeWidth={1.75} aria-hidden="true" />
        </Badge>
      </IconButton>
      <Popper
        open={isInviteMenuOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        id={menuId}
        sx={{ zIndex: 3500 }}
      >
        <ClickAwayListener
          // Treat the toggle IconButton as the popper's owner so clicking it
          // doesn't fire close-then-reopen.
          mouseEvent="onMouseDown"
          onClickAway={event => {
            const t = event.target as HTMLElement | null;
            if (t && anchorEl && anchorEl.contains(t)) return;
            setAnchorEl(null);
          }}
        >
          <section
            className="bh-invites"
            role="dialog"
            aria-label="Game invitations"
            onKeyDown={e => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setAnchorEl(null);
                bellRef.current?.focus();
              }
            }}
          >
            <header className="bh-invites__head">
              <h2 className="bh-invites__heading">Invitations</h2>
              <span className="bh-invites__count" aria-hidden="true">
                {inviteCount}
              </span>
            </header>
            {inviteCount ? (
              <ul className="bh-invites__list">
                {lastJsonMessage?.invites?.map((n, i) => (
                  <li key={[n.id || n.fromUser?.email, i].join('-')}>
                    {renderInvite(n)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="bh-invites__empty" role="status">
                No invitations right now. When a friend invites you to a
                table, it shows up here.
              </p>
            )}
          </section>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
